// juice.ts — small, dependency-free juice helpers shared by the scenes. Keep them cheap to hold
// 60fps. M2 adds hitstop, squash/stretch, a board-reveal "cooking" animation, a near-miss tension
// cue, and a results celebration — and routes the heavy ones (shake, particles, hitstop) through
// the reduced-motion setting so accessibility users get a calm board.
import Phaser from 'phaser';
import { motionAllowed } from './settings';
import { FONT } from './keys';

/**
 * #46 — the ONE multiplier-glyph helper. The type/video review caught the combo/chain multiplier
 * rendered as a lowercase ASCII `x` in some frames and the true multiplication sign `×` (U+00D7) in
 * others — mixed in the SAME frame (HUD vs stamp). Every multiplier token now routes through this so
 * the glyph is ALWAYS `×`, never `x`. Accepts a number OR a pre-formatted numeric string:
 *   mult(4) → '×4' · mult(1.5) → '×1.5' · mult('12') → '×12'.
 * Pure, dependency-free string helper — safe to import from any scene/controller (G1 HUD consumes it).
 */
export function mult(n: number | string): string {
  return `×${n}`;
}

/** Pop a display object: scale up then collapse to 0, then run an optional onComplete. */
export function popTween(
  scene: Phaser.Scene,
  obj: Phaser.GameObjects.Components.Transform & Phaser.GameObjects.GameObject,
  opts: { peak?: number; durationMs?: number; onComplete?: () => void } = {},
): void {
  const peak = opts.peak ?? 1.3;
  const duration = opts.durationMs ?? 180;
  const base = (obj as unknown as { scale: number }).scale ?? 1;
  scene.tweens.add({
    targets: obj,
    scale: { from: base, to: base * peak },
    duration: duration * 0.45,
    ease: 'Quad.easeOut',
    yoyo: false,
    onComplete: () => {
      scene.tweens.add({
        targets: obj,
        scale: 0,
        alpha: 0,
        duration: duration * 0.55,
        ease: 'Quad.easeIn',
        onComplete: () => opts.onComplete?.(),
      });
    },
  });
}

/** Combo-scaled camera shake. intensity is in pixels (~4-10 typical); dur in ms. No-op if reduced-motion. */
export function shake(camera: Phaser.Cameras.Scene2D.Camera, intensityPx: number, durMs = 120): void {
  if (!motionAllowed()) return;
  // Phaser's shake intensity is a fraction of viewport; convert from a pixel target.
  const frac = Phaser.Math.Clamp(intensityPx / Math.max(1, camera.width), 0.001, 0.05);
  camera.shake(durMs, frac);
}

/**
 * Full-screen color flash that fades out. Used for wrong-eat (red) and board-clear (white).
 *
 * Under reduced-motion we cut the intensity and lengthen the fade to a gentle wash (never a
 * blink) — WCAG 2.3.1 photosensitivity. The event always carries a non-flash visual too
 * (eaten ✓/✗ reveal, wrong-buzz VFX, board-clear cascade), so dampening loses no information.
 */
export function flash(scene: Phaser.Scene, color: number, durMs = 200, alpha = 0.55): void {
  const cam = scene.cameras.main;
  if (!motionAllowed()) {
    alpha = Math.min(alpha, 0.16);
    durMs = Math.max(durMs, 260);
  }
  // VG-5: a smoothly EASED full-screen wash (ramp up → brief hold → ramp down) instead of Phaser's
  // cam.flash, which slams to full intensity on frame 1 and reads as a jarring "solid colour fill"
  // (the video gate flagged the win/loss/hazard flashes as harsh). Peak alpha is capped soft so even
  // a big hit is a celebratory/warning PULSE, not an eye-strain slam. The shake/hitstop carry the punch.
  const peak = Math.min(alpha, 0.42);
  const ramp = Math.max(70, Math.round(durMs * 0.34));
  // A Phaser Shape renders its fill at `fillAlpha × alpha` (RectangleWebGLRenderer), so the fill MUST be
  // opaque (fillAlpha=1) with the ramp driven by the GameObject alpha — building it fillAlpha=0 and then
  // tweening alpha renders 0×alpha=0 (the wash never showed; S5 round-2 root cause, shared with the dim).
  const rect = scene.add
    .rectangle(cam.width / 2, cam.height / 2, cam.width, cam.height, color, 1)
    .setScrollFactor(0)
    .setAlpha(0)
    .setDepth(10_000);
  scene.tweens.add({
    targets: rect,
    alpha: peak,
    duration: ramp,
    ease: 'Sine.easeInOut',
    yoyo: true,
    hold: 40,
    onComplete: () => rect.destroy(),
  });
}

// A soft radial dot texture, generated ONCE per scene-texture-manager and reused by every emitter.
// Keeping it procedural means particles need no shipped asset (and the generated dot reads as a
// glowing additive spark, not a hard square). Returns '' if texture generation is unavailable
// (e.g. a headless context) so callers fall back to the legacy primitive spray.
const JUICE_DOT_KEY = '__juiceDot';
function ensureJuiceDot(scene: Phaser.Scene): string {
  if (scene.textures.exists(JUICE_DOT_KEY)) return JUICE_DOT_KEY;
  try {
    const R = 16;
    const g = scene.make.graphics({ x: 0, y: 0 }, false);
    // Bright core → transparent rim: stack circles from the outside in, alpha rising toward centre.
    for (let i = R; i >= 1; i--) {
      g.fillStyle(0xffffff, Math.pow(1 - (i - 1) / R, 1.6));
      g.fillCircle(R, R, i);
    }
    g.generateTexture(JUICE_DOT_KEY, R * 2, R * 2);
    g.destroy();
  } catch {
    return '';
  }
  return scene.textures.exists(JUICE_DOT_KEY) ? JUICE_DOT_KEY : '';
}

/** Legacy primitive spray — kept as the headless / no-texture fallback for particleBurst. */
function legacyBurst(scene: Phaser.Scene, x: number, y: number, color: number, count: number): void {
  for (let i = 0; i < count; i++) {
    const angle = (Math.PI * 2 * i) / count + Math.random() * 0.4;
    const speed = 24 + Math.random() * 48;
    const size = 3 + Math.random() * 4;
    const p = scene.add.rectangle(x, y, size, size, color).setDepth(9000);
    scene.tweens.add({
      targets: p,
      x: x + Math.cos(angle) * speed,
      y: y + Math.sin(angle) * speed,
      alpha: 0,
      scale: 0,
      duration: 320 + Math.random() * 160,
      ease: 'Quad.easeOut',
      onComplete: () => p.destroy(),
    });
  }
}

/**
 * A short burst of glowing additive sparks flung outward from (x,y) — a REAL pooled
 * `ParticleEmitter` (not hand-tweened rectangles) tinted to the event colour, so a CHOMP/threaded
 * pop reads as dense neon energy rather than flat squares. Self-destructs after the burst settles.
 * Reduced-motion keeps a tiny calm acknowledgement; a no-texture context falls back to the legacy spray.
 */
export function particleBurst(scene: Phaser.Scene, x: number, y: number, color: number, count = 10): void {
  if (!motionAllowed()) count = Math.min(count, 3);
  const key = ensureJuiceDot(scene);
  if (!key) return legacyBurst(scene, x, y, color, count);
  const emitter = scene.add
    .particles(x, y, key, {
      lifespan: { min: 280, max: 460 },
      speed: { min: 40, max: 150 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.55, end: 0 },
      alpha: { start: 1, end: 0 },
      rotate: { min: 0, max: 360 },
      tint: color,
      blendMode: 'ADD',
      emitting: false,
    })
    .setDepth(9000);
  emitter.explode(count);
  scene.time.delayedCall(520, () => emitter.destroy());
}

/**
 * COMET TRAIL — one persistent, reusable soft-dot emitter for a moving muncher. The scene creates
 * it ONCE per muncher (lazily) and starts/stops it around move tweens, so sparks only stream while
 * the sprite is actually gliding between cells — never a per-move emitter allocation. Small
 * scale→0, short life, additive, low quantity: a subtle streak that reads as motion energy without
 * occluding tile labels (the caller parks it below the muncher depths, above the tiles). Returns
 * null when the procedural dot texture is unavailable (headless) so callers keep the static line
 * trail as the only move cue. Callers gate on motionAllowed() before starting it (the emitter is
 * created inert and never emits until start()).
 */
export function createCometTrail(
  scene: Phaser.Scene,
  color: number,
  opts: { depth?: number; scaleStart?: number; alphaStart?: number; frequency?: number } = {},
): Phaser.GameObjects.Particles.ParticleEmitter | null {
  const key = ensureJuiceDot(scene);
  if (!key) return null;
  return scene.add
    .particles(0, 0, key, {
      lifespan: { min: 220, max: 340 },
      speed: { min: 4, max: 20 },
      angle: { min: 0, max: 360 },
      scale: { start: opts.scaleStart ?? 0.42, end: 0 },
      alpha: { start: opts.alphaStart ?? 0.7, end: 0 },
      quantity: 1,
      frequency: opts.frequency ?? 26,
      tint: color,
      blendMode: 'ADD',
      emitting: false,
    })
    .setDepth(opts.depth ?? 5.5);
}

/**
 * Draw a dashed rectangle outline into an existing Graphics object (cleared first), centered on
 * (0,0) for use inside a cell container. Used to read "locked / impassable" without color-coding.
 */
export function drawDashedRect(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
  color: number,
  opts: { dash?: number; gap?: number; thickness?: number; alpha?: number } = {},
): void {
  const dash = opts.dash ?? 6;
  const gap = opts.gap ?? 4;
  const thickness = opts.thickness ?? 2;
  const alpha = opts.alpha ?? 1;
  g.clear();
  g.lineStyle(thickness, color, alpha);
  const left = -w / 2;
  const top = -h / 2;
  const right = w / 2;
  const bottom = h / 2;
  const step = dash + gap;
  // top + bottom
  for (let x = left; x < right; x += step) {
    const x2 = Math.min(x + dash, right);
    g.lineBetween(x, top, x2, top);
    g.lineBetween(x, bottom, x2, bottom);
  }
  // left + right
  for (let y = top; y < bottom; y += step) {
    const y2 = Math.min(y + dash, bottom);
    g.lineBetween(left, y, left, y2);
    g.lineBetween(right, y, right, y2);
  }
}

/**
 * A brief glowing outline pulse around a cell rect (centered at 0,0 inside a container). Used for
 * the positive "threaded gauntlet" moment and for a freshly-scanned reveal. Self-destructs.
 */
export function glowPulse(
  scene: Phaser.Scene,
  parent: Phaser.GameObjects.Container,
  w: number,
  h: number,
  color: number,
  durMs = 600,
): void {
  const g = scene.add.graphics().setDepth(20);
  g.lineStyle(3, color, 1);
  g.strokeRect(-w / 2, -h / 2, w, h);
  parent.add(g);
  // S3 item 10: reduced-motion gets a STATIC single highlight (hold then fade, no expanding scale) so
  // the emphasis never strobes — the outward pulse below is motion-only.
  if (!motionAllowed()) {
    scene.tweens.add({ targets: g, alpha: 0, delay: Math.min(durMs, 300), duration: 260, onComplete: () => g.destroy() });
    return;
  }
  scene.tweens.add({
    targets: g,
    alpha: 0,
    scaleX: 1.18,
    scaleY: 1.18,
    duration: durMs,
    ease: 'Quad.easeOut',
    onComplete: () => g.destroy(),
  });
}

// VG-6b: recent floats, so a rapid combo's floats STAGGER upward instead of piling into one
// unreadable cluster over the player (the video gate's #1 remaining legibility gap). Module-level;
// entries self-prune by age, and a backwards clock (a fresh scene/board) clears stale lifts.
const recentFloats: { x: number; y: number; t: number }[] = [];

// In-flight float cards, so a board-clear can wipe leftover score floats and let the BOARD CLEAR
// hero banner read alone (the video gate flagged trailing floats cluttering the clear moment).
const activeFloatCards = new Set<Phaser.GameObjects.Container>();

// VG-2b: per-throttle-key last-fire ledger so rapid repeated floats of the SAME kind near the SAME
// cell (the gauntlet-routing skip spray) can be condensed instead of stacking a wall of
// overlapping labels over adjacent answer tiles. Decorative-only, presentation-side; keyed by the
// caller (e.g. 'safepass') so other float kinds are never throttled. Entries self-prune by age.
const floatThrottle = new Map<string, { x: number; y: number; t: number }>();

/**
 * True if a throttled float of `key` fired on/near (x,y) within `windowMs` — the caller skips this
 * one so a dense gauntlet run doesn't spray a stack of overlapping skip labels. Adjacency is
 * generous (~1.6 cells) so a tight back-to-back route condenses; a pass on a far cell still floats.
 */
function floatThrottled(scene: Phaser.Scene, key: string, x: number, y: number, windowMs: number, radius: number): boolean {
  const now = scene.time.now;
  const prev = floatThrottle.get(key);
  // A backwards clock (fresh scene/board) or an aged entry resets the throttle.
  if (prev && (now < prev.t || now - prev.t > windowMs || Math.abs(prev.x - x) > radius || Math.abs(prev.y - y) > radius)) {
    floatThrottle.delete(key);
  }
  const live = floatThrottle.get(key);
  if (live) return true; // a recent nearby one already fired — condense (skip) this one.
  floatThrottle.set(key, { x, y, t: now });
  return false;
}

/** Fade out + destroy any in-flight floats for `scene` (called on board clear so the banner reads alone). */
export function clearActiveFloats(scene: Phaser.Scene): void {
  for (const card of Array.from(activeFloatCards)) {
    if (!card || !card.scene) {
      activeFloatCards.delete(card);
      continue;
    }
    if (card.scene !== scene) continue;
    activeFloatCards.delete(card);
    scene.tweens.killTweensOf(card);
    if (!motionAllowed()) {
      card.destroy();
      continue;
    }
    scene.tweens.add({ targets: card, alpha: 0, duration: 120, onComplete: () => card.destroy() });
  }
}

/** Floating score/label text that rises and fades — branded, outlined, on a soft pill, anti-stacked. */
export function floatText(
  scene: Phaser.Scene,
  x: number,
  y: number,
  message: string,
  colorCss: string,
  fontSize = 20,
  opts: { throttleKey?: string; throttleMs?: number; clearance?: 'normal' | 'high' } = {},
): void {
  const cam = scene.cameras.main;

  // VG-2b: condense rapid repeated floats of the same KIND near the same cell (the gauntlet-routing
  // skip spray) — the throttled caller skips this one so the board's answer labels stay readable
  // during dense play. Only callers that pass a throttleKey are gated; everything else floats as before.
  if (opts.throttleKey && floatThrottled(scene, opts.throttleKey, x, y, opts.throttleMs ?? 280, fontSize * 4)) {
    return;
  }

  // Anti-stack: lift this float above any recent nearby one so concurrent floats step up the screen.
  const now = scene.time.now;
  for (let i = recentFloats.length - 1; i >= 0; i--) {
    const f = recentFloats[i];
    if (now - f.t > 760 || now < f.t) recentFloats.splice(i, 1);
  }
  let lift = 0;
  for (const f of recentFloats) {
    if (Math.abs(f.x - x) < fontSize * 5.5 && Math.abs(f.y - (y - lift)) < fontSize * 2.2) lift += fontSize * 1.85;
  }
  y -= lift;
  recentFloats.push({ x, y, t: now });

  const halfTextW = Math.max(fontSize * 2.4, message.length * fontSize * 0.34);
  const halfTextH = fontSize * 0.9;
  const inset = 8;
  const minX = cam.scrollX + halfTextW + inset;
  const maxX = cam.scrollX + cam.width - halfTextW - inset;
  const minY = cam.scrollY + halfTextH + inset;
  const maxY = cam.scrollY + cam.height - halfTextH - inset;
  const safeX = minX <= maxX ? Phaser.Math.Clamp(x, minX, maxX) : cam.scrollX + cam.width / 2;
  // VG-2c: SPAWN the float ABOVE the originating tile (not at its centre) so it is already clear of the
  // tile when it first appears — it never flashes opaque over the cell it came from, and it has less
  // distance to travel before it's off the neighbour row entirely. The lift here is in addition to the
  // anti-stack `lift` already applied to `y` above.
  const highClearance = opts.clearance === 'high';
  // Spawn a FULL tile-height above the originating cell on high-clearance floats (CHOMP/THREADED score
  // pops) so the label is already clear of the muncher + tile borders the instant it appears — fixes the
  // brief overlap of the bonus text over the player icon during fast play.
  const spawnLift = fontSize * (highClearance ? 3.1 : 1.1);
  const rawSpawnY = y - spawnLift;
  const safeY = minY <= maxY ? Phaser.Math.Clamp(rawSpawnY, minY, maxY) : cam.scrollY + cam.height / 2;
  // VG-2: rise FURTHER (was -48) so the float clears the originating tile + its neighbours sooner and
  // spends less of its life overlapping adjacent answer labels during fast play.
  const targetY = minY <= maxY ? Phaser.Math.Clamp(safeY - (highClearance ? 124 : 66), minY, maxY) : safeY - 40;
  // VG-6: branded display font + dark stroke & drop-shadow so a float reads CLEANLY over the busy grid.
  const t = scene.add
    .text(0, 0, message, {
      fontFamily: FONT.display,
      fontSize: `${fontSize}px`,
      color: colorCss,
      fontStyle: 'bold',
      stroke: '#0a0b12',
      strokeThickness: Math.max(3, fontSize * 0.18),
    })
    .setOrigin(0.5);
  t.setShadow(0, 2, '#000000', 5, false, true);
  // VG-6b/VG-2/VG-2c: a FAINT, TIGHT dark pill anchors the text's contrast WITHOUT blacking out the
  // un-eaten tile label it rises past (the text's own stroke+shadow carry legibility; the pill only
  // needs to soften the busiest cells underneath). Padding + alpha trimmed again (was 0.7/0.26 @0.2) so
  // the pill hugs the glyphs even tighter and casts the smallest, lightest shadow over adjacent words.
  const pillW = t.width + fontSize * 0.5;
  const pillH = t.height + fontSize * 0.18;
  const pill = scene.add.graphics();
  pill.fillStyle(0x0a0b12, 0.17);
  pill.fillRoundedRect(-pillW / 2, -pillH / 2, pillW, pillH, pillH / 2);
  const card = scene.add.container(safeX, safeY, [pill, t]).setDepth(9500);
  activeFloatCards.add(card);
  // Spring the card in with a small overshoot so a CHOMP/THREADED pop reads PUNCHY (the video gate's
  // "flat, lacks springiness" note). Motion-gated: under reduced-motion it snaps to full size (the float
  // still rises + fades, just without the scale spring).
  if (motionAllowed()) {
    card.setScale(highClearance ? 0.5 : 0.72);
    scene.tweens.add({ targets: card, scale: 1, duration: highClearance ? 200 : 160, ease: 'Back.easeOut' });
  }
  // High-clearance floats (score / THREADED) get a brief additive glow burst behind them for weight.
  if (highClearance && motionAllowed()) {
    const glowColor = Phaser.Display.Color.HexStringToColor(colorCss).color || 0xffffff;
    const glow = scene.add
      .circle(safeX, safeY, fontSize * 1.5, glowColor, 0.5)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setDepth(9490);
    scene.tweens.add({
      targets: glow,
      scale: 2.2,
      alpha: 0,
      duration: 320,
      ease: 'Quad.easeOut',
      onComplete: () => glow.destroy(),
    });
  }
  const destroy = (): void => {
    activeFloatCards.delete(card);
    card.destroy();
  };
  // VG-2c: FRONT-LOAD the fade. The card RISES over the full duration (so the +N/skip reads
  // climbing away from the cell), but its ALPHA is decayed FAST in the first ~40% of life on its OWN
  // tween — so by the time the float is drifting over a neighbouring tile's label it is already mostly
  // transparent, never lingering opaque over adjacent answer words. The two tweens share the card; the
  // last to finish (the rise) owns the destroy so the card is cleaned exactly once.
  scene.tweens.add({
    targets: card,
    alpha: 0,
    duration: 240, // ~43% of the 560ms rise — dim early, before it's over a neighbour
    ease: 'Quad.easeIn',
  });
  scene.tweens.add({
    targets: card,
    // Rise higher/faster so it clears adjacent labels sooner.
    y: targetY - fontSize * (highClearance ? 0.55 : 0.4),
    duration: 560,
    ease: 'Quad.easeOut',
    onComplete: destroy,
  });
}

// =============================================================================
// M2 max-juice additions
// =============================================================================

/**
 * HITSTOP — briefly freeze the sim's render cadence on a big hit (the classic 40-90ms "munch"
 * freeze) for weight. We can't pause Core (the scene must keep stepping deterministically for the
 * dev-hook/eyes harness), so we instead BRIEFLY SLOW the tween/animation clock + nudge timeScale,
 * which gives the felt "crunch" without desyncing the sim. Auto-restores. No-op under reduced-motion.
 *
 * The freeze length is clamped to a SAFE CEILING (default 110ms) so a routine munch can't hang the
 * presentation. The signature Gauntlet-Chain crescendo wants a deliberately LONGER dilation than a
 * normal munch, so it opts into a higher ceiling via `opts.ceilingMs` (the 30ms floor still applies);
 * existing callers pass no opts and keep the 110ms ceiling unchanged.
 */
export function hitstop(scene: Phaser.Scene, ms = 60, opts: { ceilingMs?: number } = {}): void {
  if (!motionAllowed()) return;
  const clampMs = Phaser.Math.Clamp(ms, 30, opts.ceilingMs ?? 110);
  // Slow the tween + time clocks (animation feel) — sim.tick() in update() is untouched, so Core
  // determinism is preserved; only the *presentation* hangs for a frame or two.
  scene.tweens.timeScale = 0.0001;
  scene.time.timeScale = 0.15;
  // Use the real wall clock to restore so the freeze length is honoured even while clocks are slowed.
  window.setTimeout(() => {
    if (!scene.scene.isActive()) return;
    scene.tweens.timeScale = 1;
    scene.time.timeScale = 1;
  }, clampMs);
}

/**
 * SQUASH & STRETCH — a chunky elastic deform that returns to base. Used on the muncher when it eats
 * and on a tile when it's munched. Honors reduced-motion by collapsing to a gentle pop.
 */
export function squashStretch(
  scene: Phaser.Scene,
  obj: Phaser.GameObjects.Components.Transform & Phaser.GameObjects.GameObject,
  opts: { amount?: number; durationMs?: number } = {},
): void {
  const o = obj as unknown as { scaleX: number; scaleY: number };
  // Rest-scale anchor: rapid re-triggers used to capture a mid-deform LIVE scale as "base" and
  // restore to it — permanently ratcheting the object's scale (same bug family as pulseMoveSquash,
  // verifier WARN 2026-07-01). Anchor once per object lifetime.
  const g = obj as unknown as Phaser.GameObjects.GameObject;
  const baseX = Number(g.getData('__sqRestX') ?? 0) || o.scaleX || 1;
  const baseY = Number(g.getData('__sqRestY') ?? 0) || o.scaleY || 1;
  g.setData('__sqRestX', baseX);
  g.setData('__sqRestY', baseY);
  const amt = (motionAllowed() ? opts.amount ?? 0.3 : (opts.amount ?? 0.3) * 0.4);
  const dur = opts.durationMs ?? 220;
  scene.tweens.add({
    targets: obj,
    scaleX: baseX * (1 + amt),
    scaleY: baseY * (1 - amt * 0.8),
    duration: dur * 0.3,
    ease: 'Quad.easeOut',
    yoyo: false,
    onComplete: () => {
      scene.tweens.add({
        targets: obj,
        scaleX: baseX,
        scaleY: baseY,
        duration: dur * 0.7,
        ease: 'Back.easeOut',
      });
    },
  });
}

/**
 * BOARD-REVEAL "cooking" animation — cascade the cells in on board start so the board feels freshly
 * served. Each cell pops from 0 with a small diagonal stagger. Returns the total duration (ms) so the
 * caller can sequence a "GO!" after it. Under reduced-motion it just fades quickly with no stagger.
 */
export function boardReveal(
  scene: Phaser.Scene,
  cells: { container: Phaser.GameObjects.Container; col: number; row: number }[],
  opts: { cols?: number; perCellMs?: number } = {},
): number {
  const reduced = !motionAllowed();
  const perCell = reduced ? 0 : opts.perCellMs ?? 26;
  let maxEnd = 0;
  for (const c of cells) {
    const obj = c.container;
    const delay = reduced ? 0 : (c.col + c.row) * perCell;
    const base = obj.scale || 1;
    obj.setScale(reduced ? 1 : 0.2).setAlpha(0);
    scene.tweens.add({
      targets: obj,
      scale: base,
      alpha: 1,
      delay,
      duration: reduced ? 140 : 230,
      ease: reduced ? 'Quad.easeOut' : 'Back.easeOut',
    });
    maxEnd = Math.max(maxEnd, delay + (reduced ? 140 : 230));
  }
  return maxEnd;
}

/**
 * NEAR-MISS / tension cue — a brief red vignette pulse around the screen edges (not a full flash) to
 * signal "danger close" without blinding the player or wrecking legibility. Self-destructs.
 */
export function vignettePulse(scene: Phaser.Scene, color: number, durMs = 420, peakAlpha = 0.4): void {
  if (!motionAllowed()) peakAlpha *= 0.5;
  const cam = scene.cameras.main;
  const w = cam.width;
  const h = cam.height;
  const thick = Math.max(18, Math.min(w, h) * 0.06);
  const g = scene.add.graphics().setScrollFactor(0).setDepth(9800).setAlpha(0);
  // four edge bands, brightest at the rim — reads as a closing frame, leaves the centre clear.
  g.fillStyle(color, 1);
  g.fillRect(0, 0, w, thick);
  g.fillRect(0, h - thick, w, thick);
  g.fillRect(0, 0, thick, h);
  g.fillRect(w - thick, 0, thick, h);
  scene.tweens.add({
    targets: g,
    alpha: { from: 0, to: peakAlpha },
    duration: durMs * 0.35,
    ease: 'Quad.easeOut',
    yoyo: true,
    hold: durMs * 0.1,
    onComplete: () => g.destroy(),
  });
}

/**
 * SHOCKWAVE RING — a thin additive stroked circle that expands outward from (x,y) to a large radius
 * while fading, over ~420ms. Used as the board-wide spectacle for the Gauntlet-Chain crescendo: a
 * single cheap GameObject (an Arc) that radiates from the threaded cell across the grid, reading as a
 * legible energy pulse (no full-screen white-out). Self-destructs on tween complete.
 *
 * Mirrors the other helpers' motion gate: under reduced-motion it draws a single faint static ring
 * that fades quickly with NO scale tween (a calm acknowledgement, never a strobe).
 */
export function shockwaveRing(
  scene: Phaser.Scene,
  x: number,
  y: number,
  color: number,
  opts: { maxRadius?: number; durationMs?: number; thickness?: number; peakAlpha?: number } = {},
): void {
  const maxRadius = opts.maxRadius ?? 240;
  const durMs = opts.durationMs ?? 420;
  const thickness = opts.thickness ?? 4;
  const peakAlpha = opts.peakAlpha ?? 0.55;
  const reduced = !motionAllowed();
  // An unfilled Arc (stroke only) is the single cheap object. Additive blend so it reads as light.
  const startR = reduced ? Math.max(8, maxRadius * 0.5) : 8;
  const ring = scene.add
    .circle(x, y, startR)
    .setStrokeStyle(thickness, color, 1)
    .setFillStyle()
    .setDepth(9600)
    .setBlendMode(Phaser.BlendModes.ADD)
    .setAlpha(reduced ? peakAlpha * 0.5 : 0);
  if (reduced) {
    // Static faint ring, quick fade, no expansion — accessible calm acknowledgement.
    scene.tweens.add({ targets: ring, alpha: 0, duration: 220, onComplete: () => ring.destroy() });
    return;
  }
  scene.tweens.add({
    targets: ring,
    radius: maxRadius,
    alpha: { from: peakAlpha, to: 0 },
    duration: durMs,
    ease: 'Quad.easeOut',
    onComplete: () => ring.destroy(),
  });
}

/**
 * RESULTS-SCREEN CELEBRATION — a falling neon-ribbon confetti shower in the palette accents (real
 * pooled emitter). Gentle, finite, self-cleaning. Under reduced-motion it routes to the calm legacy
 * path (few particles, linear fall, no gravity acceleration or spin) — never a full gravity shower.
 */
/** Legacy rect-tween confetti — headless / no-texture fallback AND the reduced-motion calm path. */
function legacyCelebrate(scene: Phaser.Scene, colors: number[], count: number, depth = 9700): void {
  const cam = scene.cameras.main;
  const w = cam.width;
  const reduced = !motionAllowed();
  for (let i = 0; i < count; i++) {
    const x = Math.random() * w;
    const size = 5 + Math.random() * 7;
    const color = colors[i % colors.length];
    const p = scene.add
      .rectangle(x, -10 - Math.random() * 60, size, size * (0.6 + Math.random()), color)
      .setDepth(depth)
      .setAngle(Math.random() * 360)
      .setScrollFactor(0);
    const fall = cam.height + 40;
    const dur = reduced ? 900 : 1400 + Math.random() * 1400;
    scene.tweens.add({
      targets: p,
      y: fall,
      x: x + (Math.random() - 0.5) * 120,
      angle: p.angle + (Math.random() - 0.5) * 540,
      delay: reduced ? 0 : Math.random() * 600,
      duration: dur,
      ease: 'Quad.easeIn',
      onComplete: () => p.destroy(),
    });
    if (!reduced) {
      scene.tweens.add({
        targets: p,
        scaleX: { from: 1, to: 0.2 },
        yoyo: true,
        repeat: -1,
        duration: 180 + Math.random() * 160,
      });
    }
  }
}

export function celebrate(scene: Phaser.Scene, colors: number[], opts: { count?: number; depth?: number } = {}): void {
  const reduced = !motionAllowed();
  const count = reduced ? 10 : opts.count ?? 60;
  // Depth: gameplay board-clear confetti sits on top (default 9700), but the Result screen passes a
  // LOW depth so the shower renders BEHIND the score readout / stat chips / links (S3 item 18) — it
  // can never bury the hero numbers, only flutter in the background gaps.
  const depth = opts.depth ?? 9700;
  // Reduced-motion: the gravity-driven emitter below is motion-only — route vestibular users to the
  // calm legacy path (few particles, linear fall, no gravity acceleration or spin).
  if (reduced) return legacyCelebrate(scene, colors, count, depth);
  const key = ensureJuiceDot(scene);
  const cam = scene.cameras.main;
  if (!key) return legacyCelebrate(scene, colors, count, depth);
  // A REAL emitter: narrow+tall+spinning particles under gravity read as fluttering neon RIBBONS,
  // not square blocks falling straight down (the video gate's "cheap victory" note). Tint cycles the
  // palette accents. Emits over a short window then stops + self-destructs.
  const emitter = scene.add
    .particles(0, 0, key, {
      x: { min: 0, max: cam.width },
      y: -12,
      lifespan: reduced ? 1000 : 2400,
      speedX: { min: -70, max: 70 },
      speedY: { min: 90, max: 260 },
      gravityY: 240,
      scaleX: { start: 0.3, end: 0.12 },
      scaleY: { start: 0.95, end: 0.4 },
      rotate: { min: 0, max: 360 },
      tint: colors,
      quantity: reduced ? 2 : 3,
      frequency: 24,
      blendMode: 'NORMAL',
      emitting: true,
    })
    .setScrollFactor(0)
    .setDepth(depth);
  scene.time.delayedCall(reduced ? 320 : 1000, () => emitter.stop());
  scene.time.delayedCall(reduced ? 1500 : 3600, () => emitter.destroy());
}

/**
 * CAMERA ZOOM-PUNCH — a quick zoom-in-and-back impulse layered on top of `shake` for the biggest
 * beats (chain crescendo, board clear, hazard hit) so they land with a distinct weighty "thump"
 * instead of yet another shake. Tiny amplitude + short, yoyo back to the exact starting zoom.
 * No-op under reduced-motion, and skipped if the camera is already mid-zoom so punches never stack.
 */
export function cameraPunch(scene: Phaser.Scene, amount = 0.035, durMs = 160): void {
  if (!motionAllowed()) return;
  const cam = scene.cameras.main;
  if (scene.tweens.isTweening(cam)) return;
  const base = cam.zoom;
  scene.tweens.add({
    targets: cam,
    zoom: base * (1 + amount),
    duration: durMs * 0.4,
    yoyo: true,
    ease: 'Quad.easeOut',
    onComplete: () => cam.setZoom(base),
  });
}

// =============================================================================
// FACE & FURY S8 — HERO WORDMARK (#12): ONE shared bespoke display cut for every clip-worthy centered
// stamp (PERFECT GAUNTLET / GAUNTLET CHAIN / CLINCHED / FLAWLESS / PHOTO FINISH). Before this the hero
// moments all shared one flat bold+stroke recipe (the never-fixed "generic type voice" WARN); now they
// wear a gold→amber two-tone gradient, tight tracking, and — for the combo beats — a broken-out ×N
// multiplier token at ~1.6× with a forward oblique lean. Static style only (no animation), so it is
// reduceMotion- AND colourblind-safe: the gradient never leaves the warm gold↔amber family and never
// encodes a gameplay state.
// =============================================================================

/** Crown sheen stop — a near-white warm gold metallic highlight at the top of the glyph band (#53). */
const HERO_SHEEN = '#fff4c8';
/** Top gradient stop — bright warm gold (a notch brighter than PALETTE.gold so the wordmark pops on veils). */
const HERO_GOLD = '#ffdf82';
/** Bottom gradient stop — a warm, bright gold-amber. #53: deliberately pulled OFF the exact armed-cell
 *  value (PALETTE.armed 0xf59e0b) toward a richer gold so the WIN/reward gold stops conflating with the
 *  armed-tile amber on back-to-back beats — still inside the warm gold↔amber family (colourblind-safe). */
const HERO_AMBER = '#f7a520';

export interface HeroCut {
  /** top gradient stop (css). default bright gold */
  top?: string;
  /** bottom gradient stop (css). default armed amber */
  bottom?: string;
  /** letter tracking (px); negative = tighter. default -0.5 */
  tracking?: number;
  /** synthesised-italic oblique lean — an energetic forward slant for combo stamps. default false */
  skew?: boolean;
  /** dark outline colour. default '#0a0b12' */
  stroke?: string;
  /** outline thickness (px). default scales with glyph height */
  strokeThickness?: number;
}

/**
 * Apply the shared HERO display cut to a Phaser Text IN PLACE and return it: a gold→amber vertical
 * two-tone gradient fill, tight tracking, a dark stroke + soft drop-shadow, and an optional subtle
 * oblique skew. Must be called AFTER the Text has its final string (the gradient is baked to the
 * current glyph height). Falls back to a flat gold on any context that can't build a canvas gradient
 * (headless), so it can never crash a capture.
 */
export function styleHeroText(t: Phaser.GameObjects.Text, cut: HeroCut = {}): Phaser.GameObjects.Text {
  const stroke = cut.stroke ?? '#0a0b12';
  // Tracking + optional lean change glyph metrics, so set them BEFORE the gradient (sized to height).
  if (typeof (t as unknown as { setLetterSpacing?: unknown }).setLetterSpacing === 'function') {
    t.setLetterSpacing(cut.tracking ?? -0.5);
  }
  if (cut.skew) t.setFontStyle(`italic ${t.style.fontStyle || 'bold'}`);
  t.setStroke(stroke, cut.strokeThickness ?? Math.max(4, t.height * 0.09));
  // Gold→amber vertical two-tone across the glyph band (baked into the text's own canvas).
  try {
    const grad = t.context.createLinearGradient(0, 0, 0, Math.max(1, t.height));
    // #53: a metallic SHEEN at the crown (near-white warm gold) over a warmer, brighter gold body so the
    // WIN/reward gold reads as bright METALLIC gold and stops conflating with the flat armed-cell amber
    // on back-to-back beats. Stays entirely in the warm gold↔amber family (no gameplay state encoded →
    // colourblind- + reduceMotion-safe). Bottom stop (HERO_AMBER) is pulled OFF PALETTE.armed (0xf59e0b).
    grad.addColorStop(0, HERO_SHEEN);
    grad.addColorStop(0.16, cut.top ?? HERO_GOLD);
    grad.addColorStop(0.55, cut.top ?? HERO_GOLD);
    grad.addColorStop(1, cut.bottom ?? HERO_AMBER);
    t.setColor(grad as unknown as string);
  } catch {
    t.setColor(cut.top ?? HERO_GOLD);
  }
  t.setShadow(0, 3, '#000000', Math.max(6, t.height * 0.14), false, true);
  return t;
}

export interface HeroWordmarkOpts {
  /** main headline (may contain \n for a stacked lockup). */
  main: string;
  /** main font size (px). */
  size: number;
  /** optional broken-out multiplier token (e.g. '×4'); rendered ~1.6× the main size with a lean. */
  mult?: string;
  /** optional smaller sub-line under the lockup (e.g. '+225'). */
  sub?: string;
  /** sub colour (css). default the amber stop. */
  subColor?: string;
  /** the shared display cut applied to the main headline (+ ×N). */
  cut?: HeroCut;
  /** also lean the main headline (default false — only the ×N token leans). */
  skewMain?: boolean;
  /** container depth. default 12_500 */
  depth?: number;
}

/**
 * Build a centered HERO WORDMARK lockup as ONE container (#12): the main headline in the shared display
 * cut, an optional ×N MULTIPLIER token broken out to its own ~1.6× glyph with a forward oblique lean
 * (the arcade combo read), and an optional smaller sub-line. The whole block is centred on the container
 * origin so the caller can pop/scale it from the middle and position/track/tear it down as one unit.
 * reduceMotion/colourblind-safe (static style only). Returns the container + its parts.
 */
export function heroWordmark(
  scene: Phaser.Scene,
  opts: HeroWordmarkOpts,
): {
  container: Phaser.GameObjects.Container;
  main: Phaser.GameObjects.Text;
  mult?: Phaser.GameObjects.Text;
  sub?: Phaser.GameObjects.Text;
} {
  const cut = opts.cut ?? {};
  const main = scene.add
    .text(0, 0, opts.main, {
      fontFamily: FONT.display,
      fontSize: `${opts.size}px`,
      color: '#ffffff',
      align: 'center',
      fontStyle: 'bold',
    })
    .setOrigin(0, 0);
  styleHeroText(main, { ...cut, skew: opts.skewMain ?? cut.skew ?? false });

  let mult: Phaser.GameObjects.Text | undefined;
  if (opts.mult) {
    mult = scene.add
      .text(0, 0, opts.mult, {
        fontFamily: FONT.display,
        fontSize: `${Math.round(opts.size * 1.6)}px`,
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);
    // The ×N always leans (energy) + a hotter gold top so the combo number is the eye-magnet.
    styleHeroText(mult, { ...cut, skew: true, top: cut.top ?? '#ffe08a' });
  }

  // Horizontal lockup: main headline, then a small gap, then the big ×N vertically centred on the band.
  const gap = opts.mult ? opts.size * 0.18 : 0;
  const multW = mult ? mult.width : 0;
  const totalW = main.width + gap + multW;
  main.setX(-totalW / 2).setY(-main.height / 2);
  mult?.setX(-totalW / 2 + main.width + gap).setY(0);

  const parts: Phaser.GameObjects.GameObject[] = mult ? [main, mult] : [main];
  let sub: Phaser.GameObjects.Text | undefined;
  if (opts.sub) {
    const numericSub = /^[+\-−]?\d/.test(opts.sub.trim());
    sub = scene.add
      .text(0, main.height / 2 + opts.size * 0.14, opts.sub, {
        fontFamily: FONT.display,
        fontSize: `${Math.round(opts.size * (numericSub ? 1 : 0.5))}px`,
        color: opts.subColor ?? HERO_AMBER,
        align: 'center',
        fontStyle: 'bold',
        stroke: '#0a0b12',
        strokeThickness: numericSub ? 4 : 3,
      })
      .setOrigin(0.5, 0);
    sub.setShadow(0, 2, '#000000', 6, false, true);
    parts.push(sub);
  }

  const container = scene.add.container(0, 0, parts).setDepth(opts.depth ?? 12_500);
  // Re-centre the whole block on the origin so pop/scale reads from the middle and any caller's
  // above-grid clamp math (which uses getBounds().height/2 as the half-height) is exact.
  const b = container.getBounds();
  const dx = -(b.x + b.width / 2);
  const dy = -(b.y + b.height / 2);
  main.x += dx;
  main.y += dy;
  if (mult) {
    mult.x += dx;
    mult.y += dy;
  }
  if (sub) {
    sub.x += dx;
    sub.y += dy;
  }
  return { container, main, mult, sub };
}
